import React from 'react';
import { Box, Link, useMediaQuery, useTheme } from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import CallIcon from '@mui/icons-material/Call';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { tokens } from '../../Components/DashboardTheme/DashboardTheme';
import { Kicker } from '../../Components/Typography/Typography';
import Reveal from '../../Components/Reveal/Reveal';
import c4uLogo from '../../img/sponsor-img/c4u-logo-registrato.svg';

const ADDRESS = 'Via Cimitero 74, 80039 Saviano (NA)';
const MAPS = 'https://www.google.com/maps/search/?api=1&query=Via+Cimitero,+74,+80039+Saviano+NA,+Italy';
const PHONE = '+39 388 370 6025';
const PHONE_HREF = 'tel:+393883706025';
const INSTAGRAM = 'https://www.instagram.com/mario__vivenzio/';
const FACEBOOK = 'https://www.facebook.com/mario.vivenzio.58/';

export default function Footer() {
    const theme = useTheme();
    const isScreenSmall = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box
            component="footer"
            id="contattiSection"
            sx={{ backgroundColor: tokens.surface, borderTop: `1px solid ${tokens.line}` }}
        >
            <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 3, md: 6 }, pt: { xs: 7, md: 10 }, pb: { xs: 4, md: 6 } }}>
                <Reveal>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: isScreenSmall ? '1fr' : '1.2fr 1fr 1fr',
                            gap: { xs: 5, md: 6 },
                        }}
                    >
                        {/* ---------- Brand ---------- */}
                        <Box>
                            <Box
                                sx={{
                                    fontFamily: tokens.display,
                                    fontSize: { xs: 40, md: 54 },
                                    lineHeight: 1,
                                    color: tokens.white,
                                }}
                            >
                                VIVENZIO GYM
                            </Box>
                            <Box
                                sx={{
                                    fontFamily: tokens.heading,
                                    fontSize: 13,
                                    letterSpacing: '0.26em',
                                    textTransform: 'uppercase',
                                    color: tokens.red,
                                    mt: 1.5,
                                }}
                            >
                                Muay Thai — Boxing
                            </Box>
                            <Box
                                sx={{
                                    fontFamily: tokens.body,
                                    fontWeight: 300,
                                    fontSize: 15,
                                    lineHeight: 1.8,
                                    color: tokens.muted,
                                    mt: 2.5,
                                    maxWidth: 340,
                                }}
                            >
                                Forti e fieri. Dall'allenamento quotidiano ai ring internazionali.
                            </Box>
                        </Box>

                        {/* ---------- Contacts ---------- */}
                        <Box>
                            <Kicker justifyContent="flex-start" color={tokens.dim}>Contatti</Kicker>
                            <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                                <FooterLink href={MAPS} icon={<LocationOnIcon fontSize="small" />}>
                                    {ADDRESS}
                                </FooterLink>
                                <FooterLink href={PHONE_HREF} icon={<CallIcon fontSize="small" />}>
                                    {PHONE}
                                </FooterLink>
                            </Box>
                        </Box>

                        {/* ---------- Social ---------- */}
                        <Box>
                            <Kicker justifyContent="flex-start" color={tokens.dim}>Seguici</Kicker>
                            <Box sx={{ mt: 2.5, display: 'flex', gap: 1 }}>
                                <SocialTile href={INSTAGRAM} label="Instagram"><InstagramIcon /></SocialTile>
                                <SocialTile href={FACEBOOK} label="Facebook"><FacebookIcon /></SocialTile>
                            </Box>
                        </Box>
                    </Box>
                </Reveal>
            </Box>

            {/* ---------- Bottom bar ---------- */}
            <Box sx={{ borderTop: `1px solid ${tokens.line}` }}>
                <Box
                    sx={{
                        maxWidth: 1280,
                        mx: 'auto',
                        px: { xs: 3, md: 6 },
                        py: 3,
                        display: 'flex',
                        flexDirection: isScreenSmall ? 'column' : 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2.5,
                    }}
                >
                    <Box sx={{ fontFamily: tokens.body, fontSize: 12.5, color: tokens.dim, textAlign: 'center' }}>
                        © {new Date().getFullYear()} Vivenzio Gym. All Rights Reserved.
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: isScreenSmall ? 'column' : 'row',
                            alignItems: 'center',
                            gap: 1.5,
                        }}
                    >
                        <Box sx={{ fontFamily: tokens.body, fontSize: 12.5, color: tokens.dim }}>
                            Sviluppato dal team di
                        </Box>
                        <Link
                            href="https://condominioforyou.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Condominio For You"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: '#FFFFFF',
                                borderRadius: '4px',
                                px: 1.25,
                                py: 0.75,
                                transition: `transform 0.35s ${tokens.ease}`,
                                ':hover': { transform: 'translateY(-1px)' },
                            }}
                        >
                            <Box component="img" src={c4uLogo} alt="Condominio For You" sx={{ height: 18, width: 'auto', display: 'block' }} />
                        </Link>
                        <Box sx={{ display: 'flex', gap: 0.75, fontFamily: tokens.body, fontSize: 12.5 }}>
                            <Link
                                href="https://www.linkedin.com/in/daniele-farriciello/"
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="none"
                                sx={{ color: tokens.muted, ':hover': { color: tokens.red } }}
                            >
                                Daniele Farriciello
                            </Link>
                            <Box component="span" sx={{ color: tokens.dim }}>&</Box>
                            <Link
                                href="https://www.linkedin.com/in/davide-farriciello/"
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="none"
                                sx={{ color: tokens.muted, ':hover': { color: tokens.red } }}
                            >
                                Davide Farriciello
                            </Link>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

function FooterLink({ href, icon, children }) {
    return (
        <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                fontFamily: tokens.body,
                fontWeight: 300,
                fontSize: 15,
                color: tokens.muted,
                transition: `color 0.35s ${tokens.ease}`,
                ':hover': { color: tokens.white },
                '& svg': { color: tokens.red },
            }}
        >
            {icon}
            {children}
        </Link>
    );
}

function SocialTile({ href, label, children }) {
    return (
        <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            underline="none"
            sx={{
                display: 'grid',
                placeItems: 'center',
                width: 46,
                height: 46,
                color: tokens.muted,
                border: `1px solid ${tokens.line}`,
                transition: `all 0.35s ${tokens.ease}`,
                ':hover': {
                    color: tokens.white,
                    borderColor: tokens.red,
                    backgroundColor: 'rgba(224, 26, 34, 0.12)',
                    transform: 'translateY(-2px)',
                },
            }}
        >
            {children}
        </Link>
    );
}
